import { Head } from 'vite-react-ssg'
import { getCanonicalUrl } from '../../utils/canonical'

export interface GuideFeature {
  key: 'screenreader' | 'keyboard' | 'live' | 'contrast' | 'text'
  label: string
  text: string
}

export interface GuideFaq {
  q: string
  a: string
}

export interface GuideData {
  path: string
  seoTitle: string
  headline: string
  description: string
  topicLabel: string
  lead: string
  understanding: string[]
  features: GuideFeature[]
  mobile: string
  legal: string
  business: string
  conclusion: string
  faqs: GuideFaq[]
}

/* ---------- Inline SVG visuals (decorative → aria-hidden; diagrams → role=img+title) ---------- */

function FeatureIcon({ kind }: { kind: GuideFeature['key'] }) {
  const common = {
    width: 28,
    height: 28,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  }
  switch (kind) {
    case 'screenreader':
      return (
        <svg {...common}>
          <path d="M4 10v4M8 7v10M12 4v16" />
          <path d="M16 9a4 4 0 0 1 0 6" />
          <path d="M19 6a8 8 0 0 1 0 12" />
        </svg>
      )
    case 'keyboard':
      return (
        <svg {...common}>
          <rect x="2" y="6" width="20" height="12" rx="2" />
          <path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M8 14h8" />
        </svg>
      )
    case 'live':
      return (
        <svg {...common}>
          <path d="M3 11a9 9 0 0 1 18 0" />
          <circle cx="12" cy="12" r="2" />
          <path d="M12 14v6" />
        </svg>
      )
    case 'contrast':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor" stroke="none" />
        </svg>
      )
    case 'text':
      return (
        <svg {...common}>
          <path d="M4 7V5h16v2M9 5v14M7 19h4" />
          <path d="M14 12V11h6v1M17 11v8M15.5 19h3" />
        </svg>
      )
  }
}

function HeroArt() {
  return (
    <svg
      viewBox="0 0 480 240"
      className="w-full max-w-md h-auto"
      role="img"
      aria-label="Illustration of a web interface being made accessible"
    >
      <defs>
        <linearGradient id="hg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#eef2ff" />
          <stop offset="1" stopColor="#e0e7ff" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="480" height="240" rx="16" fill="url(#hg)" />
      {/* browser window */}
      <rect x="60" y="46" width="300" height="150" rx="10" fill="#ffffff" stroke="#c7d2fe" />
      <rect x="60" y="46" width="300" height="26" rx="10" fill="#eef2ff" />
      <circle cx="76" cy="59" r="4" fill="#a5b4fc" />
      <circle cx="90" cy="59" r="4" fill="#a5b4fc" />
      <circle cx="104" cy="59" r="4" fill="#a5b4fc" />
      {/* content rows */}
      <rect x="78" y="88" width="160" height="10" rx="5" fill="#c7d2fe" />
      <rect x="78" y="108" width="220" height="8" rx="4" fill="#e0e7ff" />
      <rect x="78" y="124" width="200" height="8" rx="4" fill="#e0e7ff" />
      {/* focused button with ring */}
      <rect x="78" y="150" width="96" height="28" rx="6" fill="#4f46e5" />
      <rect x="73" y="145" width="106" height="38" rx="9" fill="none" stroke="#6366f1" strokeWidth="2" strokeDasharray="4 3" />
      <rect x="94" y="161" width="64" height="6" rx="3" fill="#c7d2fe" />
      {/* soundwave = screen reader */}
      <g stroke="#4f46e5" strokeWidth="3" strokeLinecap="round">
        <path d="M388 108a26 26 0 0 1 0 24" fill="none" />
        <path d="M404 98a44 44 0 0 1 0 44" fill="none" />
      </g>
      <circle cx="378" cy="120" r="9" fill="#4f46e5" />
    </svg>
  )
}

function ContrastDemo() {
  return (
    <figure className="rounded-xl border border-gray-200 bg-white p-4">
      <svg viewBox="0 0 300 96" className="w-full h-auto" role="img" aria-label="Low contrast text fails; high contrast text passes">
        <rect x="0" y="0" width="145" height="96" rx="8" fill="#e5e7eb" />
        <text x="16" y="42" fontSize="22" fontWeight="700" fill="#c2c6cd">Aa</text>
        <text x="16" y="70" fontSize="12" fill="#9ca3af">2.1:1 · fails</text>
        <rect x="155" y="0" width="145" height="96" rx="8" fill="#111827" />
        <text x="171" y="42" fontSize="22" fontWeight="700" fill="#ffffff">Aa</text>
        <text x="171" y="70" fontSize="12" fill="#a7f3d0">7.0:1 · passes</text>
      </svg>
      <figcaption className="mt-2 text-sm text-gray-500">Body text needs a contrast ratio of at least 4.5:1.</figcaption>
    </figure>
  )
}

function FocusDemo() {
  return (
    <figure className="rounded-xl border border-gray-200 bg-white p-4">
      <svg viewBox="0 0 300 96" className="w-full h-auto" role="img" aria-label="A button without a focus indicator and a button with a visible focus ring">
        <rect x="16" y="34" width="110" height="34" rx="7" fill="#4f46e5" />
        <text x="71" y="56" fontSize="12" fill="#e0e7ff" textAnchor="middle">no focus</text>
        <rect x="174" y="34" width="110" height="34" rx="7" fill="#4f46e5" />
        <rect x="168" y="28" width="122" height="46" rx="10" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeDasharray="5 3" />
        <text x="229" y="56" fontSize="12" fill="#e0e7ff" textAnchor="middle">focused</text>
      </svg>
      <figcaption className="mt-2 text-sm text-gray-500">Keyboard users need a clearly visible focus indicator.</figcaption>
    </figure>
  )
}

function AnnounceDemo() {
  return (
    <figure className="rounded-xl border border-gray-200 bg-white p-4">
      <svg viewBox="0 0 300 96" className="w-full h-auto" role="img" aria-label="A state change passes through an ARIA live region and is announced">
        <rect x="6" y="34" width="80" height="34" rx="7" fill="#eef2ff" stroke="#c7d2fe" />
        <text x="46" y="55" fontSize="11" fill="#4338ca" textAnchor="middle">state ↑</text>
        <path d="M90 51h24" stroke="#818cf8" strokeWidth="2" markerEnd="url(#ar)" />
        <rect x="116" y="34" width="84" height="34" rx="7" fill="#eef2ff" stroke="#c7d2fe" />
        <text x="158" y="52" fontSize="10" fill="#4338ca" textAnchor="middle">ARIA live</text>
        <path d="M204 51h24" stroke="#818cf8" strokeWidth="2" markerEnd="url(#ar)" />
        <rect x="230" y="34" width="64" height="34" rx="7" fill="#4f46e5" />
        <text x="262" y="55" fontSize="11" fill="#ffffff" textAnchor="middle">announced</text>
        <defs>
          <marker id="ar" markerWidth="7" markerHeight="7" refX="5" refY="3" orient="auto">
            <path d="M0 0l6 3-6 3z" fill="#818cf8" />
          </marker>
        </defs>
      </svg>
      <figcaption className="mt-2 text-sm text-gray-500">Dynamic results must be announced, not just shown.</figcaption>
    </figure>
  )
}

const BADGES = ['WCAG 2.2 AA', 'AODA', 'ADA', 'Accessible Canada Act']

/* ---------- Layout ---------- */

export default function AccessibilityGuideLayout({ data }: { data: GuideData }) {
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: data.headline,
        description: data.description,
        author: { '@type': 'Organization', name: 'achecker.ca' },
        publisher: { '@type': 'Organization', name: 'achecker.ca' },
        mainEntityOfPage: getCanonicalUrl(data.path),
      },
      {
        '@type': 'FAQPage',
        mainEntity: data.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  }

  return (
    <>
      <Head>
        <title>{data.seoTitle}</title>
        <meta name="description" content={data.description} />
        <meta name="robots" content="index, noarchive, follow" />
        <link rel="canonical" href={getCanonicalUrl(data.path)} />
      </Head>

      <article className="max-w-3xl mx-auto py-8 space-y-14 text-gray-800">
        {/* Hero */}
        <header className="rounded-3xl bg-gradient-to-br from-indigo-50 to-blue-50 p-8 sm:p-10">
          <div className="flex flex-col sm:flex-row items-center gap-8">
            <div className="flex-1 space-y-4">
              <span className="inline-block text-xs font-semibold tracking-wide uppercase text-indigo-600 bg-white/70 rounded-full px-3 py-1">
                Accessibility Guide
              </span>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">{data.headline}</h1>
              <p className="text-lg text-gray-600 leading-relaxed">{data.lead}</p>
            </div>
            <div className="flex-shrink-0 w-full sm:w-auto"><HeroArt /></div>
          </div>
        </header>

        {/* Understanding */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-900">Understanding {data.topicLabel.toLowerCase()} accessibility</h2>
          {data.understanding.map((p, i) => (
            <p key={i} className="leading-relaxed">{p}</p>
          ))}
        </section>

        {/* Features grid */}
        <section className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Key accessibility features</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {data.features.map((f) => (
              <div key={f.key} className="rounded-xl border border-gray-200 p-5 space-y-2">
                <div className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-11 h-11 rounded-lg bg-indigo-50 text-indigo-600">
                    <FeatureIcon kind={f.key} />
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900">{f.label}</h3>
                </div>
                <p className="text-sm leading-relaxed text-gray-600">{f.text}</p>
              </div>
            ))}
          </div>

          {/* Visual band — the same three diagrams on both pages */}
          <div className="grid sm:grid-cols-3 gap-4 pt-2">
            <ContrastDemo />
            <FocusDemo />
            <AnnounceDemo />
          </div>
        </section>

        {/* Mobile */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-900">Accessibility on mobile</h2>
          <p className="leading-relaxed">{data.mobile}</p>
        </section>

        {/* Legal */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-900">The legal and regulatory context</h2>
          <p className="leading-relaxed">{data.legal}</p>
          <ul className="flex flex-wrap gap-2 pt-1 list-none p-0 m-0">
            {BADGES.map((b) => (
              <li key={b} className="text-sm font-medium text-indigo-700 bg-indigo-50 rounded-full px-3 py-1">{b}</li>
            ))}
          </ul>
        </section>

        {/* Business */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-900">The business and future case</h2>
          <p className="leading-relaxed">{data.business}</p>
        </section>

        {/* Conclusion */}
        <section className="rounded-2xl bg-gray-50 border border-gray-200 p-6 space-y-3">
          <h2 className="text-2xl font-bold text-gray-900">Conclusion</h2>
          <p className="leading-relaxed">{data.conclusion}</p>
        </section>

        {/* FAQ accordion */}
        <section className="space-y-3">
          <h2 className="text-2xl font-bold text-gray-900">Frequently asked questions</h2>
          <div className="divide-y divide-gray-200 border-t border-gray-200">
            {data.faqs.map((f) => (
              <details key={f.q} className="group py-3">
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <h3 className="text-base font-semibold text-gray-900 pr-4">{f.q}</h3>
                  <span className="text-indigo-500 transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                </summary>
                <p className="mt-2 leading-relaxed text-gray-600">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      </article>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </>
  )
}
